import { useEffect, useMemo, useState } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  EditOutlined as EditIcon,
  DeleteOutlined as DeleteIcon,
  Shortcut as RedirectIcon,
  ContentCopy as CopyIcon,
} from "@mui/icons-material";

import PropTypes from "prop-types";
import DataTable from "@accredo/fe-shared/components/DataTable.jsx";
import { useSnackbar } from "notistack";
import { useMultiTableData } from "./tables/hooks/useMultiTableData.jsx";
import { applyFilterWithAccessorKeys } from "./tables/utils/tableUtils.js";
import { backendGet } from "@accredo/fe-shared/services/dataService.js";
import { useUser } from "@accredo/fe-shared/hooks/user.js";
import { getUserRowsPerPage } from "../utils/userSettings.js";
import { useSubstitutionActions } from "../hooks/useSubstitutionActions.jsx";

/**
 * SubstitutionsTable Component
 * Displays a table of user substitutions with different presentation modes
 *
 * @param {string} variant - Display variant:
 *   - "compact": Simplified view (no filters, limited rows, specific scope)
 *   - "full": Complete view with filters, pagination, scope selector for admins
 *   - "bare": Same as compact but without border, for use inside cards
 * @param {string} scope - Scope for compact variant:
 *   - "substituting_for": Shows users I am substituting for
 *   - "substituted_by": Shows users who are substituting for me
 * @param {string} title - Title for the compact variant table
 * @param {number} limit - Maximum number of rows to display (only for compact variant)
 * @param {array} data - Optional pre-fetched data (for compact/bare variants only)
 * @param {function} onEdit - Callback when edit button is clicked (receives substitution object)
 * @param {function} onDelete - Callback when delete button is clicked (receives substitution object)
 */
export default function SubstitutionsTable({
  variant = "full",
  scope = null,
  title = null,
  limit = 5,
  data,
  onEdit,
  onDelete,
  refetchTrigger,
}) {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUser();
  const [filterScope, setFilterScope] = useState("mine"); // "mine" or "all"
  const [internalRefetchTrigger, setInternalRefetchTrigger] = useState(0);
  const [compactData, setCompactData] = useState([]);
  const [compactLoading, setCompactLoading] = useState(false);
  const [compactLoaded, setCompactLoaded] = useState(false);

  const isCompact = variant === "compact" || variant === "bare";
  const isFull = variant === "full";
  const isBare = variant === "bare";

  const isSysAdmin = user?.role_names?.includes("sysadmin");
  const isCustomerAdmin = user?.role_names?.includes("kd_verwaltung");
  const canViewAll = isSysAdmin || isCustomerAdmin;

  const tableKey = isCompact
    ? `substitutions_compact_${scope}`
    : "substitutions_full";
  const storageKey = isCompact
    ? `v2_table_substitutions_compact_${scope}`
    : "v2_table_substitutions_full";
  const defaultRowsPerPage = getUserRowsPerPage(user, 5);

  const triggerRefetch = () => {
    setInternalRefetchTrigger((prev) => prev + 1);
  };

  const {
    state: substitutionState,
    setState: setSubstitutionListState,
    compactState,
    setCompactState,
    deleteSubstitution,
  } = useSubstitutionActions();

  // Fetch data for compact variant (only if data prop not provided)
  useEffect(() => {
    if (!isCompact || !scope || data !== undefined) {
      return;
    }

    const fetchCompactData = async () => {
      setCompactLoading(true);
      try {
        const response = await backendGet("/data/substitutions/own", {
          label: "Stellvertretungen laden",
          enqueueSnackbar,
        });

        if (response?.[scope]) {
          setCompactData(response[scope]);
          // also sync into state
          setCompactState(scope, response[scope]);
        }
        setCompactLoaded(true);
      } catch (error) {
        console.error("Failed to fetch substitutions:", error);
        setCompactData([]);
        setCompactLoaded(true);
      } finally {
        setCompactLoading(false);
      }
    };

    fetchCompactData();
  }, [isCompact, scope, internalRefetchTrigger, refetchTrigger, data]);

  // Use provided data prop if available
  useEffect(() => {
    if (data !== undefined && isCompact) {
      setCompactData(data);
      setCompactLoaded(true);
      setCompactLoading(false);
    }
  }, [data, isCompact]);

  const tablesConfig = useMemo(() => {
    let route = "/data/substitutions/own";
    const extraParams = {
      sort: [
        {
          field: "starts_at",
          order: "desc",
        },
      ],
      include: ["user", "substitute", "creator"],
    };

    if (isFull) {
      if (canViewAll && filterScope === "all") {
        route = "/data/substitutions/all";
      } else {
        // For full variant with "mine" scope, use the unified route with filter parameter
        route = "/data/substitutions/all";
        extraParams.filters = { scope: "mine" };
      }
    }

    return [
      {
        key: tableKey,
        label: "Stellvertretungen",
        route: route,
        pageSize: isCompact ? limit : defaultRowsPerPage,
        storageKey: storageKey,
        extraParams,
        // For full table, sync fetched rows into backendData (substitution-page.data.list)
        onStateChange: isFull ? setSubstitutionListState : undefined,
      },
    ];
  }, [
    isCompact,
    isFull,
    limit,
    filterScope,
    canViewAll,
    storageKey,
    tableKey,
    defaultRowsPerPage,
  ]);

  const { tableStates, updateTableState } = useMultiTableData(
    tablesConfig,
    enqueueSnackbar
  );

  const tableState = tableStates[tableKey] || {};

  // Derive rows for full table:
  // - Prefer atom backed substitutionState.data when available
  // - Fallback to tableState.rows (initial load before onStateChange runs)
  const fullRowsFromAtom = Array.isArray(substitutionState?.data)
    ? substitutionState.data
    : null;
  const fullRows = fullRowsFromAtom ?? tableState.rows ?? [];
  const fullTotalRows =
    substitutionState?.meta?.total ?? tableState.totalRows ?? fullRows.length;

  // Derive rows for compact tables:
  // - Prefer atom-backed compactState[scope] when available
  // - Fallback to local compactData (initial load)
  const compactRowsFromAtom = scope ? compactState?.[scope] : null;
  const compactRows =
    Array.isArray(compactRowsFromAtom) && compactRowsFromAtom.length > 0
      ? compactRowsFromAtom
      : compactData;

  // Use different data sources for compact vs full variant
  const rows = isCompact ? compactRows : fullRows;
  const totalRows = isCompact ? rows.length : fullTotalRows;
  const pageIndex = isCompact ? 0 : (tableState.pageIndex ?? 0);
  const pageSize = isCompact ? limit : (tableState.pageSize ?? 10);
  const isLoading = isCompact
    ? compactLoading
    : (tableState.isLoading ?? false);
  const hasLoaded = isCompact ? compactLoaded : (tableState.hasLoaded ?? false);

  useEffect(() => {
    if (!isFull) {
      return;
    }

    // Reset pagination and trigger refetch when filters change for full variant
    updateTableState(tableKey, { pageIndex: 0, shouldFetch: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFull, filterScope, tableKey, internalRefetchTrigger, refetchTrigger]);

  // Format date helper
  const formatDate = (date) => {
    if (!date) {
      return null;
    }
    return new Date(date).toLocaleDateString("de-DE");
  };

  // Format date range helper
  const formatDateRange = (starts_at, ends_at) => {
    const start = formatDate(starts_at);
    const end = formatDate(ends_at);

    if (!start && !end) {
      return "Unbefristet";
    }
    if (!start) {
      return `bis ${end}`;
    }
    if (!end) {
      return `ab ${start}`;
    }
    if (start === end) {
      return start;
    }
    return `${start} - ${end}`;
  };

  // Handle delete action
  const handleDelete = async (substitution) => {
    if (onDelete) {
      onDelete(substitution);
    } else {
      // Default delete behavior
      if (
        window.confirm("Möchten Sie diese Stellvertretung wirklich löschen?")
      ) {
        await deleteSubstitution(substitution.user_id, substitution.id);
        triggerRefetch();
      }
    }
  };

  // Handle edit action
  const handleEdit = (substitution) => {
    if (onEdit) {
      onEdit(substitution);
    }
  };

  // Define columns for compact variant
  const compactColumns = useMemo(() => {
    const name_column =
      scope === "substituting_for"
        ? {
            accessorKey: "user.full_name",
            header: "Benutzer",
            size: 200,
            Cell: ({ row }) => {
              const target_user = row.original?.user;
              return (
                <Typography variant="body2" sx={{ fontWeight: 400 }}>
                  {target_user?.full_name || "-"}
                </Typography>
              );
            },
          }
        : {
            accessorKey: "substitute.full_name",
            header: "Stellvertreter",
            size: 200,
            Cell: ({ row }) => {
              const substitute = row.original?.substitute;
              return (
                <Typography variant="body2" sx={{ fontWeight: 400 }}>
                  {substitute?.full_name || "-"}
                </Typography>
              );
            },
          };

    return [
      name_column,
      {
        accessorKey: "date_range",
        header: "Zeitraum",
        size: 200,
        Cell: ({ row }) => {
          const date_range = formatDateRange(
            row.original.starts_at,
            row.original.ends_at
          );
          return (
            <Typography variant="body2" sx={{ fontWeight: 400 }}>
              {date_range}
            </Typography>
          );
        },
      },
    ];
  }, [scope]);

  // Define columns for full variant
  const fullColumns = useMemo(
    () => [
      {
        accessorKey: "user.full_name",
        header: "Benutzer",
        size: 200,
        Cell: ({ row }) => {
          const target_user = row.original?.user;
          return (
            <Typography variant="body2" sx={{ fontWeight: 400 }}>
              {target_user?.full_name || "-"}
            </Typography>
          );
        },
      },
      {
        accessorKey: "substitute.full_name",
        header: "Stellvertreter",
        size: 200,
        Cell: ({ row }) => {
          const substitute = row.original?.substitute;
          return (
            <Typography variant="body2" sx={{ fontWeight: 400 }}>
              {substitute?.full_name || "-"}
            </Typography>
          );
        },
      },
      {
        accessorKey: "starts_at",
        header: "Beginn",
        size: 120,
        Cell: ({ cell }) => (
          <Typography variant="body2">
            {formatDate(cell.getValue()) || "Sofort"}
          </Typography>
        ),
      },
      {
        accessorKey: "ends_at",
        header: "Ende",
        size: 120,
        Cell: ({ cell }) => (
          <Typography variant="body2">
            {formatDate(cell.getValue()) || "Unbefristet"}
          </Typography>
        ),
      },
      {
        accessorKey: "notification_mode",
        header: "Modus",
        size: 120,
        filterVariant: "select",
        filterSelectOptions: [
          { text: "Umleiten", value: "redirect" },
          { text: "Kopieren", value: "copy" },
        ],
        Cell: ({ cell }) => {
          const mode = cell.getValue();
          const is_redirect = mode === "redirect";
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {is_redirect ? (
                <RedirectIcon fontSize="small" />
              ) : (
                <CopyIcon fontSize="small" />
              )}
              <Typography variant="body2">
                {is_redirect ? "Umleiten" : "Kopieren"}
              </Typography>
            </Box>
          );
        },
      },
      {
        accessorKey: "grace_periods",
        header: "Karenz (AT)",
        size: 220,
        enableSorting: false,
        Cell: ({ row }) => (
          <Typography variant="body2">
            Beginn: {row.original.notification_grace_period_start_days ?? 0} ·
            Ende: {row.original.notification_grace_period_end_days ?? 0} ·
            Zugriff Beginn: {row.original.access_grace_period_start_days ?? 0} ·
            Zugriff Ende: {row.original.access_grace_period_end_days ?? 0}
          </Typography>
        ),
      },
      {
        accessorKey: "creator.full_name",
        header: "Erstellt von",
        size: 180,
        Cell: ({ row }) => {
          const creator = row.original?.creator;
          return (
            <Typography variant="body2" sx={{ fontWeight: 400 }}>
              {creator?.full_name || "-"}
            </Typography>
          );
        },
      },
      {
        accessorKey: "actions",
        header: "Aktionen",
        size: 100,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", gap: 1 }}>
            <IconButton
              size="small"
              onClick={() => handleEdit(row.original)}
              sx={{ color: theme.palette.text.secondary }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => handleDelete(row.original)}
              sx={{ color: theme.palette.text.secondary }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ),
      },
    ],
    [theme]
  );

  const columns = isCompact ? compactColumns : fullColumns;

  // Filter tabs for full variant (only for admins)
  const filterTabs = useMemo(() => {
    if (!isFull || !canViewAll) {
      return undefined;
    }

    return [
      {
        key: "mine",
        label: "Meine",
        onClick: setFilterScope,
      },
      {
        key: "all",
        label: "Alle",
        onClick: setFilterScope,
      },
    ];
  }, [isFull, canViewAll]);

  // Pagination change handler
  const handlePaginationChange = (pagination) => {
    const next =
      typeof pagination === "function"
        ? pagination({ pageIndex, pageSize })
        : pagination;

    if (!next) {
      return;
    }

    updateTableState(tableKey, {
      pageIndex: next.pageIndex,
      pageSize: next.pageSize,
    });
  };

  return (
    <DataTable
      columns={columns}
      data={rows}
      isLoading={isLoading}
      hasLoaded={hasLoaded}
      title={title}
      variant={isBare ? "bare" : "enclosed"}
      enableHeader={isFull}
      enableTopToolbar={isFull}
      enableBottomToolbar={isFull}
      enableFooter={isFull}
      enableRowActions={false}
      enableSorting={isFull}
      enableGlobalFilter={isFull}
      enableColumnFilters={isFull}
      enableColumnHiding={isFull}
      enablePagination={isFull}
      manualPagination={isFull}
      pageIndex={pageIndex}
      pageSize={pageSize}
      totalRows={totalRows}
      onChangePage={handlePaginationChange}
      storageKey={storageKey}
      onChangeSorting={(sorting) =>
        updateTableState(tableKey, {
          sorting,
        })
      }
      onChangeFilters={(filters) =>
        updateTableState(tableKey, {
          filters: applyFilterWithAccessorKeys(filters, columns),
        })
      }
      onChangeGlobalFilter={(searchword) =>
        updateTableState(tableKey, {
          searchword,
        })
      }
      filterTabs={filterTabs}
      activeFilterTab={filterScope}
    />
  );
}

SubstitutionsTable.propTypes = {
  variant: PropTypes.oneOf(["compact", "full", "bare"]),
  scope: PropTypes.oneOf(["substituting_for", "substituted_by"]),
  title: PropTypes.string,
  limit: PropTypes.number,
  data: PropTypes.array,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  refetchTrigger: PropTypes.number,
};
